import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Button, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import {
  loadInsuranceEquipments,
  REMOVE_EQUIPMENT_FROM_INSURANCE
} from 'src/store/actions/insuranceActions';
import useDebounce from 'src/hooks/useDebounce';
import { selectInsuranceEquipmentQueryOptions } from 'src/store/selectors/insuranceSelector';
import { useParams } from 'react-router-dom';
import { Add, Remove } from '@mui/icons-material';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import SearchBarByType from 'src/components/SearchBarByType';
import { setEquipmentOptions } from 'src/store/actions/equipmentsActions';

import useStyles from './styles';
import AssociateEquipmentDialog from '../Insurances/AssociateEquipmentToInsurance/associateEquipmentDialog';
import RemoveEquipmentAssociationDialog from '../Insurances/RemoveEquipmentsOfInsurance/removeEquipmentAsociationDialog';

const Toolbar = ({ className, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { policyId } = useParams();
  const queryOptions = useSelector(selectInsuranceEquipmentQueryOptions);
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  const [openRemoveEquipment, setOpenRemoveEquipment] = useState(false);
  const isAsociating = useSelector(
    isLoadingSelector([REMOVE_EQUIPMENT_FROM_INSURANCE.action])
  );

  const searchTypes = [
    { value: 'description', label: 'DESCRIPTION', type: 'text' },
    { value: 'equipmentId', label: 'ID', type: 'number' }
  ];

  const debounceSearch = useDebounce(
    (search, queryOpts) =>
      dispatch(loadInsuranceEquipments({ policyId, ...queryOpts, ...search })),
    400
  );

  useEffect(() => {
    dispatch(loadInsuranceEquipments({ policyId }));
  }, [dispatch, policyId]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleOpenRemoveEquipment = useCallback(() => {
    setOpenRemoveEquipment(true);
  }, [setOpenRemoveEquipment]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleCloseRemoveEquipment = useCallback(() => {
    setOpenRemoveEquipment(false);
  }, [setOpenRemoveEquipment]);

  const handleSearchCriteria = useCallback(() => {
    setSearchValue('');
    const clearedOptions = { ...queryOptions };
    searchTypes.forEach(type => {
      clearedOptions[type.value] = '';
    });
    dispatch(setEquipmentOptions(clearedOptions));
    dispatch(loadInsuranceEquipments({ policyId, ...clearedOptions, page: 0 }));
  }, [dispatch, queryOptions, searchTypes, policyId]);

  const handleSearch = useCallback(
    (e, criteria) => {
      const { value } = e.target;
      setSearchValue(value);
      debounceSearch({ [criteria]: value }, { ...queryOptions, page: 0 });
    },
    [setSearchValue, debounceSearch, queryOptions]
  );

  return (
    <>
      <div className={clsx(classes.root, className)} {...rest}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '3rem'
          }}
        >
          <Typography
            style={{ fontWeight: '400' }}
            color="primary"
            variant="h1"
          >
            {t('INSURANCE_VIEW.TITLE')}
          </Typography>
          <div
            className={classes.actionsContainer}
            style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
          >
            <div style={{ width: '100%' }}>
              <SearchBarByType
                types={searchTypes}
                handleSearch={handleSearch}
                searchValue={searchValue}
                handleSearchCriteria={handleSearchCriteria}
              />
            </div>
            <Tooltip
              title={t('INSURANCE_VIEW.ASSOCIATE_EQUIPMENT_TO_INSURANCE')}
            >
              <Button
                className={clsx(classes.buttons, classes.outlined)}
                onClick={handleOpen}
                size="small"
                style={{ minWidth: 'auto' }}
                color="primary"
                disabled={isAsociating}
              >
                <Add className={classes.icon} />
              </Button>
            </Tooltip>
            <Tooltip title={t('INSURANCE_VIEW.REMOVE_EQUIPMENT_OF_INSURANCE')}>
              <Button
                className={clsx(classes.buttons, classes.outlined)}
                onClick={handleOpenRemoveEquipment}
                size="small"
                style={{ minWidth: 'auto' }}
                color="secondary"
                disabled={isAsociating}
              >
                <Remove className={classes.icon} />
              </Button>
            </Tooltip>
          </div>
        </div>
        <AssociateEquipmentDialog
          open={open}
          onClose={handleClose}
          policyId={policyId}
        />
        <RemoveEquipmentAssociationDialog
          open={openRemoveEquipment}
          onClose={handleCloseRemoveEquipment}
          policyId={policyId}
        />
      </div>
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string,
  insurance: PropTypes.object
};

export default Toolbar;
